import {
  Button,
  LoadingSpinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiLineSegment, PiWarning } from 'react-icons/pi';
import React, { useMemo } from 'react';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import {
  DrawGeometryType,
  getLineStringWarnings,
} from '@ais-ui/react-openlayers';
import { useMapSnapshot } from './useMapSnapshot';
import { cn, createGeoJsonFromGeometry, Geometry } from '@ais-ui/utils';
import { UseFormReturn } from 'react-hook-form';
import { TextGeometryPopover } from './TextGeometryPopover';
import { useLocalization } from '@ais-ui/locale-management';

interface DrawLineFieldProps {
  form?: UseFormReturn<any>;
  geometry: Geometry | null;
  onStartDrawing: (
    geometryType: DrawGeometryType,
    shape?: GeoJson | undefined | null,
    maxPoints?: number,
  ) => void;
  maxPoints?: number;
  color: string;
}

export const DrawLineField = ({
  form,
  geometry,
  onStartDrawing,
  maxPoints,
  color,
}: DrawLineFieldProps) => {
  const { messages } = useLocalization();
  const shape = useMemo(() => {
    if (geometry) {
      return createGeoJsonFromGeometry(geometry);
    }
    return null;
  }, [geometry]);

  const { snapshot, isLoading } = useMapSnapshot(shape, color);

  const validationWarning = useMemo(() => {
    if (geometry?.type === 'LineString' && geometry.coordinates) {
      return getLineStringWarnings(geometry.coordinates as number[][]);
    }
    return null;
  }, [geometry]);

  return (
    <div className="space-y-2">
      {validationWarning && (
        <div className="flex items-center justify-end mb-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-amber-600 cursor-help">
                <PiWarning className="size-5" />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {validationWarning}
            </TooltipContent>
          </Tooltip>
        </div>
      )}
      <div
        className={cn(
          'flex space-x-4 justify-center',
          !snapshot && !isLoading && 'justify-start',
        )}
      >
        {isLoading && <LoadingSpinner size="small"></LoadingSpinner>}
        {!snapshot && !isLoading && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-10"
            onClick={() => onStartDrawing('LineString', shape, maxPoints)}
          >
            <PiLineSegment className="size-6"></PiLineSegment>
          </Button>
        )}
        {snapshot && !isLoading && (
          <div className="relative">
            <img
              alt={`Shape Preview`}
              className="rounded-md"
              height="160px"
              src={snapshot}
              style={{
                objectFit: 'cover',
              }}
              width="420px"
            />

            <div className="absolute bottom-1 right-1 flex flex-row-reverse space-x-reverse space-x-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    id="draw-edit-shape-button"
                    type="button"
                    variant="outline"
                    size="icon"
                    className="size-10"
                    onClick={() => onStartDrawing('LineString', shape, maxPoints)}
                  >
                    <PiLineSegment className="size-6"></PiLineSegment>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {messages.textDrawingFromDialogButtonText}
                </TooltipContent>
              </Tooltip>
              {form && shape && (
                <TextGeometryPopover
                  shape={shape}
                  geometryType={'LineString'}
                  form={form}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
